import React, { useEffect, useState } from "react";
import { Package, User, MapPin, MessageCircle, Send, ShieldCheck, PackageSearch, StickyNote, Trash2, Truck, Calendar } from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { api } from "../api";
import { useApp } from "../context/AppContext";
import { InquiryAccessLink } from "../components/InquiryAccessLink";

const STATUSES = ["New", "In Review", "Picked Up", "Delivered"];
const TRACK_STATUSES = ["Checking", "Picked up", "In transit", "Out for delivery", "Delivered"];

export function SubmissionCard({ s, onChange }) {
  const { corridors, user } = useApp();
  const [reply, setReply] = useState(s.reply || "");
  const [note, setNote] = useState("");
  const [notes, setNotes] = useState(s.notes || []);
  const route = corridors.find((c) => c.id === s.route);

  const save = async () => { await api.updateSubmission(s.id, { reply }); onChange && onChange(); toast.success("Reply saved. Customer can see it in their dashboard."); };
  const setStatus = async (v) => { await api.updateSubmission(s.id, { status: v }); onChange && onChange(); toast.success(`Status → ${v}`); };
  const del = async () => {
    if (!window.confirm("Delete this customer request permanently? This cannot be undone.")) return;
    try { await api.deleteSubmission(s.id); toast.success("Request deleted"); onChange && onChange(); }
    catch { toast.error("Could not delete request"); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    try { const updated = await api.addSubmissionNote(s.id, note.trim()); setNotes(updated.notes || []); setNote(""); toast.success("Internal note added"); }
    catch { toast.error("Could not add note"); }
  };

  return (
    <div className="glass rounded-2xl p-6">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2 text-xl font-display font-semibold">{s.senderCountry || route?.from || "?"} <span className="text-orange-500">→</span> {s.receiverCountry || route?.to || "?"}</div>
          {(route?.name || s.route) && <p className="text-sm text-neutral-600 mt-0.5">{route?.name || s.route}</p>}
          <p className="text-xs text-neutral-500 mt-0.5">{new Date(s.createdAt).toLocaleString()} · from {s.userEmail}</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={s.status} onValueChange={setStatus}>
            <SelectTrigger className="w-36 bg-black/[0.03] border-black/10 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}</SelectContent>
          </Select>
          {user?.role === "admin" && (
            <Button onClick={del} variant="outline" size="icon" data-testid={`submission-delete-${s.id}`} className="border-red-200 text-red-600 hover:bg-red-50 h-9 w-9 shrink-0"><Trash2 className="w-4 h-4" /></Button>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 text-sm">
        <div className="glass rounded-xl p-4">
          <p className="text-orange-400 font-medium flex items-center gap-1.5 mb-2"><User className="w-3.5 h-3.5" /> Sender</p>
          <p>{s.senderName} {s.senderId && `· ID ${s.senderId}`}</p>
          <p className="text-neutral-600">{s.senderPhone}</p>
          <p className="text-neutral-600 flex items-start gap-1 mt-1"><MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {s.senderAddress}{s.senderCity ? `, ${s.senderCity}` : ""}{s.senderPostal ? ` ${s.senderPostal}` : ""}</p>
        </div>
        <div className="glass rounded-xl p-4">
          <p className="text-orange-400 font-medium flex items-center gap-1.5 mb-2"><User className="w-3.5 h-3.5" /> Receiver</p>
          <p>{s.receiverName} {s.receiverId && `· ID ${s.receiverId}`}</p>
          <p className="text-neutral-600">{s.receiverPhone}</p>
          <p className="text-neutral-600 flex items-start gap-1 mt-1"><MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {s.receiverAddress}{s.receiverCity ? `, ${s.receiverCity}` : ""}{s.receiverPostal ? ` ${s.receiverPostal}` : ""}</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-sm mt-3 text-neutral-600">
        {s.serviceType && <span className="flex items-center gap-1.5 font-medium text-neutral-800"><Truck className="w-4 h-4 text-orange-500" /> {s.serviceType}</span>}
        {(s.pickupDate || s.pickupTime) && <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4 text-orange-500" /> {s.pickupDate || "?"}{s.pickupTime ? ` · ${s.pickupTime}` : ""}</span>}
        {s.insurance && <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-orange-500" /> {s.insurance}{s.insurancePrice ? ` (${s.insurancePrice})` : ""}</span>}
        {s.itemValue && <span>Value: {s.itemValue}</span>}
        {s.sentBy && <span className="flex items-center gap-1.5 text-orange-700 bg-orange-500/10 px-2 py-0.5 rounded-md" data-testid={`submission-sentby-${s.id}`}><User className="w-3.5 h-3.5" /> Sent by {s.sentBy}</span>}
      </div>
      <div className="mt-3 rounded-xl border border-black/10 bg-black/[0.02] p-3">
        <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500 flex items-center gap-1.5 mb-2"><Package className="w-3.5 h-3.5 text-orange-500" /> {(s.boxes?.length || 1)} box{(s.boxes?.length || 1) > 1 ? "es" : ""}</p>
        {(s.boxes?.length ? s.boxes : [{ weight: s.weight, length: s.length, width: s.width, height: s.height, contents: s.contents }]).map((b, i) => (
          <div key={i} className="text-sm text-neutral-700 flex flex-wrap gap-x-2">
            <span className="font-medium">Box {i + 1}:</span>
            <span>{b.weight || "?"}kg · {b.length || "?"}x{b.width || "?"}x{b.height || "?"}cm</span>
            {b.contents && <span className="text-neutral-500">— {b.contents}</span>}
          </div>
        ))}
      </div>

      <div className="mt-4">
        <Textarea value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Write a reply / quote for the customer…" className="bg-black/[0.03] border-black/10 min-h-20" />
        <div className="flex gap-2 mt-2">
          <Button onClick={save} className="orange-gradient text-white border-0 gap-2 hover:opacity-90 h-9"><Send className="w-4 h-4" /> Save reply</Button>
          <a href={`https://wa.me/${(s.senderPhone||'').replace(/\D/g,'')}`} target="_blank" rel="noreferrer">
            <Button variant="outline" className="border-neutral-300 text-neutral-800 hover:bg-black/[0.04] gap-2 h-9"><MessageCircle className="w-4 h-4 text-orange-500" /> WhatsApp customer</Button>
          </a>
        </div>
      </div>

      {/* Internal staff notes */}
      <div className="mt-4 pt-4 border-t border-black/10">
        <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500 flex items-center gap-1.5 mb-2"><StickyNote className="w-3.5 h-3.5 text-orange-500" /> Internal notes <span className="font-normal normal-case text-neutral-400">· visible to staff & admin only</span></p>
        {notes.length > 0 && (
          <div className="space-y-2 mb-3">
            {notes.map((n) => (
              <div key={n.id} className="bg-orange-500/[0.06] border border-orange-500/15 rounded-lg px-3 py-2" data-testid={`note-${n.id}`}>
                <p className="text-sm text-neutral-700">{n.text}</p>
                <p className="text-[11px] text-neutral-500 mt-1">{n.author}{n.authorRole ? ` (${n.authorRole})` : ""} · {new Date(n.createdAt).toLocaleString()}</p>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <input value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addNote(); }}
            placeholder="Add an internal note for the team…" data-testid={`note-input-${s.id}`}
            className="flex-1 h-9 rounded-lg border border-black/10 bg-black/[0.03] text-sm px-3 focus:outline-none focus:ring-2 focus:ring-orange-500/40" />
          <Button onClick={addNote} data-testid={`note-add-${s.id}`} variant="outline" className="border-neutral-300 text-neutral-800 hover:bg-black/[0.04] gap-1.5 h-9"><StickyNote className="w-4 h-4 text-orange-500" /> Add note</Button>
        </div>
      </div>
    </div>
  );
}

export function TrackingCard({ t: initial, onActivity, onDeleted }) {
  const { user } = useApp();
  const [t, setT] = useState(initial);
  const [note, setNote] = useState("");

  const setStatus = async (v) => {
    setT((prev) => ({ ...prev, status: v }));
    try { await api.updateTracking(t.id, { status: v }); toast.success(`Tracking status → "${v}"`); onActivity && onActivity(); }
    catch { toast.error("Could not update"); }
  };
  const del = async () => {
    if (!window.confirm("Delete this tracking request permanently? This cannot be undone.")) return;
    try { await api.deleteTracking(t.id); toast.success("Tracking request deleted"); onDeleted ? onDeleted(t.id) : (onActivity && onActivity()); }
    catch { toast.error("Could not delete tracking request"); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    try { const u = await api.addTrackingNote(t.id, note.trim()); setT(u); setNote(""); toast.success("Internal note added"); onActivity && onActivity(); }
    catch { toast.error("Could not add note"); }
  };

  return (
    <div className="glass rounded-2xl p-5" data-testid={`tracking-card-${t.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl orange-gradient grid place-items-center shrink-0"><PackageSearch className="w-5 h-5 text-white" /></div>
          <div>
            <p className="font-display font-semibold">#{t.trackingNumber}</p>
            <p className="text-sm text-neutral-600">{t.name || "—"} · {t.phone || "no phone"} · {t.country || "no country"}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {t.phone && (
            <a href={`https://wa.me/${(t.phone||'').replace(/\D/g,'')}`} target="_blank" rel="noreferrer">
              <Button variant="outline" size="icon" className="border-neutral-300 text-neutral-800 hover:bg-black/[0.04] h-9 w-9"><MessageCircle className="w-4 h-4 text-orange-500" /></Button>
            </a>
          )}
          <select
            data-testid={`tracking-status-${t.id}`}
            value={t.status || "Checking"}
            onChange={(e) => setStatus(e.target.value)}
            className="h-9 rounded-lg border border-black/10 bg-white text-sm px-3 font-medium text-neutral-800 focus:outline-none focus:ring-2 focus:ring-orange-500/40"
          >
            {TRACK_STATUSES.map((st) => <option key={st} value={st}>{st}</option>)}
          </select>
          {user?.role === "admin" && (
            <Button onClick={del} variant="outline" size="icon" data-testid={`tracking-delete-${t.id}`} className="border-red-200 text-red-600 hover:bg-red-50 h-9 w-9 shrink-0"><Trash2 className="w-4 h-4" /></Button>
          )}
          <p className="text-xs text-neutral-400 whitespace-nowrap">{new Date(t.createdAt).toLocaleString()}</p>
        </div>
      </div>

      {/* Internal notes */}
      <div className="mt-4 pt-4 border-t border-black/10">
        <p className="text-xs font-semibold uppercase tracking-wider text-neutral-500 flex items-center gap-1.5 mb-2"><StickyNote className="w-3.5 h-3.5 text-orange-500" /> Internal notes <span className="font-normal normal-case text-neutral-400">· staff & admin only</span></p>
        {(t.notes || []).length > 0 && (
          <div className="space-y-2 mb-3">
            {t.notes.map((n) => (
              <div key={n.id} className="bg-orange-500/[0.06] border border-orange-500/15 rounded-lg px-3 py-2" data-testid={`tracking-note-${n.id}`}>
                <p className="text-sm text-neutral-700">{n.text}</p>
                <p className="text-[11px] text-neutral-500 mt-1">{n.author}{n.authorRole ? ` (${n.authorRole})` : ""} · {new Date(n.createdAt).toLocaleString()}</p>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <input value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addNote(); }}
            placeholder="Add an internal note for the team…" data-testid={`tracking-note-input-${t.id}`}
            className="flex-1 h-9 rounded-lg border border-black/10 bg-black/[0.03] text-sm px-3 focus:outline-none focus:ring-2 focus:ring-orange-500/40" />
          <Button onClick={addNote} data-testid={`tracking-note-add-${t.id}`} variant="outline" className="border-neutral-300 text-neutral-800 hover:bg-black/[0.04] gap-1.5 h-9"><StickyNote className="w-4 h-4 text-orange-500" /> Add note</Button>
        </div>
      </div>
    </div>
  );
}

export default function StaffDashboard() {
  const { user } = useApp();
  const [subs, setSubs] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [filter, setFilter] = useState("All");

  const load = () => api.getSubmissions().then(setSubs).catch(() => setSubs([]));
  const loadTracks = () => api.getTracking().then(setTracks).catch(() => setTracks([]));
  useEffect(() => { load(); loadTracks(); }, []);

  const shown = filter === "All" ? subs : subs.filter((s) => s.status === filter);

  return (
    <div className="max-w-5xl mx-auto px-5 py-12">
      <div className="mb-8">
        <p className="text-orange-500 text-sm font-semibold uppercase tracking-wider">Staff Panel</p>
        <h1 className="font-display font-bold text-3xl mt-2">Customer requests</h1>
        <p className="text-neutral-600 mt-1">Welcome {user?.name} — you handle {user?.countries?.length ? user.countries.join(", ") : "assigned"} regions. Review requests and update customers.</p>
        <InquiryAccessLink />
      </div>

      <div className="flex gap-2 flex-wrap mb-6">
        {["All", ...STATUSES].map((st) => (
          <button key={st} onClick={() => setFilter(st)} className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${filter === st ? "orange-gradient text-white border-transparent" : "border-neutral-300 text-neutral-700 hover:bg-black/[0.04]"}`}>{st}</button>
        ))}
      </div>
      {shown.length ? (
        <div className="space-y-4">{shown.map((s) => <SubmissionCard key={s.id} s={s} onChange={load} />)}</div>
      ) : (
        <div className="glass rounded-2xl p-12 text-center"><Package className="w-12 h-12 text-neutral-300 mx-auto mb-4" /><p className="text-neutral-600">No requests in this view yet.</p></div>
      )}

      <div className="mt-12">
        <div className="flex items-center gap-2 mb-1"><PackageSearch className="w-5 h-5 text-orange-500" /><h2 className="font-display font-bold text-2xl">Tracking requests</h2></div>
        <p className="text-neutral-600 text-sm mb-6">Package tracking requests from customers in your regions. Update the status so customers stay informed.</p>
        {tracks.length ? (
          <div className="space-y-3">
            {tracks.map((t) => <TrackingCard key={t.id} t={t} />)}
          </div>
        ) : (
          <div className="glass rounded-2xl p-12 text-center"><PackageSearch className="w-12 h-12 text-neutral-300 mx-auto mb-4" /><p className="text-neutral-600">No tracking requests in your regions yet.</p></div>
        )}
      </div>
    </div>
  );
}
